import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Skeleton } from './Skeleton';

export interface Column<T> {
  /** Stable key — also used as the React key for the cell. */
  key: string;
  header: ReactNode;
  /** Cell renderer. Gets the whole row so it can combine fields. */
  render: (row: T) => ReactNode;
  /** Hide below `sm` — for columns that matter less on a phone. */
  hideOnMobile?: boolean;
  align?: 'left' | 'center' | 'right';
  className?: string;
  headerClassName?: string;
  /**
   * Pins the column against one edge while the rest of the table scrolls under
   * it — for a wide register where "which row am I on" and "what can I do to
   * it" must stay in view however far right the reader has scrolled.
   *
   * Pin sparingly. Every pinned column is width the scrolling middle does not
   * get, and a table pinned on both edges with nothing left between them is a
   * table that no longer scrolls.
   */
  sticky?: 'left' | 'right';
  /**
   * The pinned column's width in pixels. Required with `sticky`: the offsets
   * are stacked from these, and a column that measured itself would shift every
   * time its content changed length.
   */
  stickyWidth?: number;
}

/**
 * A heading spanning several columns, drawn in a row above them — "Fee details"
 * over Total, Paid, Due and Status.
 *
 * Only worth it where the columns under it are meaningless apart: four money
 * columns need saying whose money once, not four times. Columns outside every
 * group keep their own heading, which stretches down through both rows.
 */
export interface ColumnGroup {
  label: ReactNode;
  /** The column keys under it. They must be adjacent in `columns`. */
  keys: string[];
}

export interface TableProps<T> {
  columns: Column<T>[];
  /** Adds a spanning row above the headings. Omit for the usual single row. */
  groups?: ColumnGroup[];
  rows: T[];
  /** Unique key per row — never the array index. */
  rowKey: (row: T) => string;
  rowClassName?: (row: T) => string | undefined | false;
  rowPinClassName?: (row: T) => string | undefined | false;
  isLoading?: boolean;
  /** Rendered in place of the body when there are no rows and no loading. */
  empty?: ReactNode;
  onRowClick?: (row: T) => void;
  /**
   * `default` draws its own panel around the table. `cards` floats each row as
   * its own tile. `plain` is `default`'s row treatment with no panel of its own
   * — for a caller that already owns the panel and puts something else, a
   * heading or its pagination, inside the same one.
   */
  variant?: 'default' | 'cards' | 'plain';
  className?: string;
}

/**
 * The table every list screen uses.
 *
 * Handles its own loading and empty states so no screen reimplements them, and
 * scrolls horizontally inside its own container — the page body must never
 * scroll sideways on a phone.
 */
export function Table<T>({
  columns,
  groups,
  rows,
  rowKey,
  rowClassName,
  rowPinClassName,
  isLoading = false,
  empty,
  onRowClick,
  variant = 'default',
  className,
}: TableProps<T>) {
  const showEmpty = !isLoading && rows.length === 0;
  const isCards = variant === 'cards';

  /** Which group a column belongs to, so the two header rows can agree. */
  const groupOf = (key: string) => groups?.find((group) => group.keys.includes(key));

  /**
   * Where each pinned column comes to rest.
   *
   * Left-pinned columns stack from the left edge, so each one sits past the
   * width of every pinned column before it; right-pinned columns stack the
   * other way. Computed once here rather than written into each column, because
   * inserting a column would otherwise mean re-adding up every offset after it
   * by hand.
   *
   * `cards` is left out: its rows are separate tiles with spacing between them,
   * and a pinned cell would float over the gaps.
   */
  const canPin = !isCards && columns.some((column) => column.sticky);

  const pinOffsets = new Map<string, { side: 'left' | 'right'; offset: number }>();
  if (canPin) {
    let fromLeft = 0;
    columns.forEach((column) => {
      if (column.sticky !== 'left') return;
      pinOffsets.set(column.key, { side: 'left', offset: fromLeft });
      fromLeft += column.stickyWidth ?? 0;
    });

    let fromRight = 0;
    [...columns].reverse().forEach((column) => {
      if (column.sticky !== 'right') return;
      pinOffsets.set(column.key, { side: 'right', offset: fromRight });
      fromRight += column.stickyWidth ?? 0;
    });
  }

  /** The last column pinned to each edge, which carries the dividing rule. */
  const lastPinnedLeft = canPin
    ? columns.filter((column) => column.sticky === 'left').at(-1)?.key
    : undefined;
  const firstPinnedRight = canPin
    ? columns.find((column) => column.sticky === 'right')?.key
    : undefined;

  /**
   * What a pinned cell needs beyond its offset.
   *
   * An opaque background, or the scrolling columns show straight through it,
   * and a rule on the inner edge so the reader can see where the frozen part
   * ends. The background is a token, so both themes work with no `dark:`.
   */
  function pinClasses(column: Column<T>, tone: 'head' | 'body', row?: T): string | false {
    const pin = pinOffsets.get(column.key);
    if (!pin) return false;

    const customPin = row && rowPinClassName ? rowPinClassName(row) : undefined;

    return cn(
      'sm:sticky sm:z-20',
      tone === 'head'
        ? 'sm:bg-slate-100 dark:sm:bg-[#0b1322] sm:z-30'
        // The hover tint has to stay fully opaque: a translucent one lets the
        // columns scrolling underneath the pinned cell show straight through it.
        : customPin || 'sm:bg-white dark:sm:bg-[#111c2e] sm:group-hover:bg-slate-50 dark:sm:group-hover:bg-[#16253d]',
      column.key === lastPinnedLeft && 'sm:border-r sm:border-slate-200 dark:sm:border-slate-800 sm:shadow-2xs',
      column.key === firstPinnedRight && 'sm:border-l sm:border-slate-200 dark:sm:border-slate-800 sm:shadow-2xs',
    );
  }

  function pinStyle(column: Column<T>) {
    const pin = pinOffsets.get(column.key);
    if (!pin) return undefined;

    return pin.side === 'left'
      ? { left: pin.offset, width: column.stickyWidth, minWidth: column.stickyWidth }
      : { right: pin.offset, width: column.stickyWidth, minWidth: column.stickyWidth };
  }

  const headCell = (column: Column<T>) =>
    cn(
      isCards
        ? 'px-4 py-3.5 text-[14px] font-bold tracking-wide text-slate-700 dark:text-slate-300 first:rounded-l-2xl last:rounded-r-2xl'
        : 'whitespace-nowrap px-4 py-3.5 text-left text-[14px] font-bold tracking-wide text-slate-700 dark:text-slate-300',
      column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left',
      column.hideOnMobile && 'hidden sm:table-cell',
      pinClasses(column, 'head'),
      column.headerClassName,
    );

  return (
    <div className={cn(variant === 'default' ? 'card overflow-hidden' : 'w-full', className)}>
      <div className="overflow-x-auto table-scrollbar">
        <table
          className={cn(
            'w-full text-sm min-w-[700px] sm:min-w-full',
            isCards ? 'border-separate border-spacing-y-3.5' : 'border-collapse',
            // Pinned columns only mean anything if there is something to scroll
            // past them, so the table takes its content's width rather than the
            // container's and lets the wrapper above scroll.
            canPin && 'min-w-[850px] sm:min-w-max',
          )}
        >
          <thead>
            {groups && groups.length > 0 && (
              <tr className={cn(isCards ? '' : 'border-b border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-[#0b1322] text-[14px] font-bold tracking-wide text-slate-700 dark:text-slate-300 align-middle')}>
                {columns.map((column, index) => {
                  const group = groupOf(column.key);

                  // A grouped column contributes a cell only where its group
                  // starts; the rest of the span is the `colSpan` below.
                  if (group) {
                    if (group.keys[0] !== column.key) return null;
                    const isGroupHidden = group.keys.every((k) => columns.find((c) => c.key === k)?.hideOnMobile);
                    return (
                      <th
                        key={`group-${column.key}`}
                        scope="colgroup"
                        colSpan={group.keys.length}
                        className={cn(
                          'whitespace-nowrap border-b border-slate-200 dark:border-slate-800 px-4 py-2 text-center text-[14px] font-bold tracking-wide text-slate-700 dark:text-slate-300 align-middle',
                          isGroupHidden && 'hidden sm:table-cell',
                        )}
                      >
                        {group.label}
                      </th>
                    );
                  }

                  // Ungrouped headings stretch down through both rows, so the
                  // strip reads as one header rather than two.
                  return (
                    <th
                      key={`${column.key}-${index}`}
                      scope="col"
                      rowSpan={2}
                      style={pinStyle(column)}
                      className={cn(headCell(column), 'align-middle')}
                    >
                      {column.header}
                    </th>
                  );
                })}
              </tr>
            )}

            <tr className={cn(isCards ? 'bg-slate-100 dark:bg-[#0b1322] rounded-2xl' : 'border-b border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-[#0b1322] text-[14px] font-bold tracking-wide text-slate-700 dark:text-slate-300 align-middle')}>
              {columns
                // With a group row above, the ungrouped headings have already
                // been drawn with a `rowSpan` and must not be repeated here.
                .filter((column) => (groups && groups.length > 0 ? Boolean(groupOf(column.key)) : true))
                .map((column) => (
                  <th key={column.key} scope="col" style={pinStyle(column)} className={headCell(column)}>
                    {column.header}
                  </th>
                ))}
            </tr>
          </thead>

          <tbody>
            {isLoading &&
              Array.from({ length: 6 }, (_, rowIndex) => (
                <tr
                  key={`skeleton-${rowIndex}`}
                  className={cn(
                    isCards
                      ? 'bg-white dark:bg-slate-900 rounded-2xl shadow-2xs'
                      : 'border-b border-line last:border-0',
                  )}
                >
                  {columns.map((column, colIdx) => (
                    <td
                      key={column.key}
                      style={pinStyle(column)}
                      className={cn(
                        'px-5 py-4',
                        isCards && [
                          'bg-white dark:bg-slate-900 border-y border-slate-200 dark:border-slate-800',
                          colIdx === 0 && 'rounded-l-2xl border-l',
                          colIdx === columns.length - 1 && 'rounded-r-2xl border-r',
                        ],
                        column.hideOnMobile && 'hidden sm:table-cell',
                        pinClasses(column, 'body'),
                      )}
                    >
                      <Skeleton className="h-3.5 w-full max-w-[140px]" />
                    </td>
                  ))}
                </tr>
              ))}

            {!isLoading &&
              rows.map((row) => (
                <tr
                  key={rowKey(row)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={cn(
                    isCards
                      ? 'bg-white dark:bg-slate-900 rounded-2xl shadow-2xs hover:shadow-md transition-shadow duration-200 group'
                      : 'border-b border-slate-200/80 dark:border-slate-800/80 bg-white dark:bg-slate-900 transition-colors duration-150 ease-in-out hover:bg-slate-50/80 dark:hover:bg-slate-800/60 group',
                    onRowClick && 'cursor-pointer',
                    rowClassName?.(row),
                  )}
                >
                  {columns.map((column, colIdx) => (
                    <td
                      key={column.key}
                      style={pinStyle(column)}
                      className={cn(
                        'px-5 py-2 align-middle text-ink',
                        isCards && [
                          'bg-white dark:bg-slate-900 border-y border-slate-200 dark:border-slate-800',
                          colIdx === 0 && 'rounded-l-2xl border-l',
                          colIdx === columns.length - 1 && 'rounded-r-2xl border-r',
                        ],
                        column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left',
                        column.hideOnMobile && 'hidden sm:table-cell',
                        pinClasses(column, 'body', row),
                        column.className,
                      )}
                    >
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {showEmpty && empty}
    </div>
  );
}
